/* =============================================================================
   Vantage — cart behaviour
   Copyright (c) 2026. All rights reserved.

   All cart mutations funnel through updateCart(), which calls the Ajax API,
   asks Shopify to re-render the affected sections, and broadcasts `cart:change`
   so every listening element refreshes itself from one source of truth.
   ========================================================================== */

(function (V) {
  'use strict';

  /**
   * Section ids are decided by the JSON template, not by the file name — the
   * cart page's "main-cart" section is really called "main". So the ids are
   * read off the DOM, where Liquid has written the true value.
   */
  function cartNodes() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-cart-section]'));
  }

  function activeSections() {
    return cartNodes()
      .map(function (node) {
        return node.getAttribute('data-section-id');
      })
      .filter(Boolean);
  }

  function paintCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(function (node) {
      node.textContent = cart.item_count;
      node.classList.toggle('is-hidden', cart.item_count === 0);
    });
    document.querySelectorAll('.cart-icon-container').forEach(function (container) {
      var emptySvg = container.getAttribute('data-icon-empty');
      var filledSvg = container.getAttribute('data-icon-filled');
      
      if (cart.item_count > 0) {
        container.innerHTML = filledSvg;
      } else {
        container.innerHTML = emptySvg;
      }
    });
  }

  function refresh(cart) {
    paintCount(cart);
    var ids = activeSections();
    if (!ids.length) {
      V.emit('cart:change', { cart: cart });
      return Promise.resolve(cart);
    }

    return V.render(ids)
      .then(function (payload) {
        ids.forEach(function (id) {
          var html = payload[id];
          if (!html) return;
          var selector = '[data-section-id="' + id + '"]';
          var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
          var current = document.querySelector(selector);
          if (incoming && current) current.innerHTML = incoming.innerHTML;
        });
        V.emit('cart:change', { cart: cart });
        return cart;
      })
      .catch(function () {
        V.emit('cart:change', { cart: cart });
        return cart;
      });
  }

  function fetchCart() {
    return V.request(V.routes.cart + '.js');
  }

  function addToCart(formData) {
    return fetch(V.routes.cartAdd + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            var error = new Error(data.description || data.message || V.strings.cartError);
            error.data = data;
            throw error;
          }
          return data;
        });
      })
      .then(function (item) {
        return fetchCart().then(refresh).then(function () {
          V.emit('cart:added', { item: item });
          return item;
        });
      });
  }

  function changeLine(payload) {
    return V.request(V.routes.cartChange + '.js', { method: 'POST', body: payload }).then(refresh);
  }

  V.cart = { fetch: fetchCart, add: addToCart, change: changeLine, refresh: refresh };

  /* ---------------------------------------------------------------------
     <vt-add-to-cart> — wraps the product form, submits over Ajax.
     --------------------------------------------------------------------- */

  V.define(
    'vt-add-to-cart',
    class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.button = this.querySelector('[type="submit"]');
        this.errorBox = this.querySelector('[data-form-error]');
        if (!this.form) return;

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submit();
        });
      }

      submit() {
        if (this.button && this.button.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(true);
        this.showError('');

        V.cart
          .add(new FormData(this.form))
          .then(() => {
            V.announce(V.strings.cartAdded);
            var drawer = document.getElementById('cart-drawer');
            if (drawer && drawer.open && this.dataset.openDrawer !== 'false') {
              drawer.open();
            }
          })
          .catch((error) => this.showError(error.message))
          .finally(() => this.setLoading(false));
      }

      setLoading(state) {
        if (!this.button) return;
        this.button.dataset.loading = String(state);
        this.button.disabled = state;
      }

      showError(message) {
        if (!this.errorBox) return;
        this.errorBox.textContent = message;
        this.errorBox.classList.toggle('is-hidden', !message);
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-quantity> — stepper bound to a number input.
     --------------------------------------------------------------------- */

  V.define(
    'vt-quantity',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        if (!this.input) return;

        this.addEventListener('click', (event) => {
          var step = event.target.closest('[data-step]');
          if (!step) return;
          this.nudge(Number(step.dataset.step));
        });

        this.input.addEventListener('change', () => this.commit());
      }

      nudge(delta) {
        var min = Number(this.input.min || 0);
        var max = this.input.max ? Number(this.input.max) : Infinity;
        var next = Math.min(max, Math.max(min, Number(this.input.value || 0) + delta));
        if (next === Number(this.input.value)) return;
        this.input.value = next;
        this.commit();
      }

      commit() {
        var line = this.dataset.line;
        if (!line) return;
        var row = this.closest('.cart-line');
        if (row) row.setAttribute('data-updating', '');
        V.cart.change({ line: Number(line), quantity: Number(this.input.value) }).catch(function () {
          if (row) row.removeAttribute('data-updating');
        });
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-cart-remove> — removes a line without a page reload.
     --------------------------------------------------------------------- */

  V.define(
    'vt-cart-remove',
    class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          event.preventDefault();
          var row = this.closest('.cart-line');
          if (row) row.setAttribute('data-updating', '');
          V.cart.change({ line: Number(this.dataset.line), quantity: 0 }).then(function () {
            V.announce(V.strings.cartRemoved);
          });
        });
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-cart-note> — saves the order note as the shopper types.
     --------------------------------------------------------------------- */

  V.define(
    'vt-cart-note',
    class extends HTMLElement {
      connectedCallback() {
        var field = this.querySelector('textarea');
        if (!field) return;

        field.addEventListener(
          'input',
          V.debounce(function () {
            V.request(V.routes.cartUpdate + '.js', {
              method: 'POST',
              body: { note: field.value }
            });
          }, 500)
        );
      }
    }
  );

  /* ---------------------------------------------------------------------
     Open the drawer whenever an item is added from anywhere.
     --------------------------------------------------------------------- */

  V.on('cart:added', function () {
    var bubble = document.querySelector('.cart-toggle');
    if (!bubble) return;
    bubble.classList.remove('is-pulsing');
    void bubble.offsetWidth;
    bubble.classList.add('is-pulsing');
  });
})(window.Vantage);
